/**
 * Field rounds (Prompt 8): golf and ten-pin leaderboards. The server ranks everything; these are the
 * calls and the shapes it answers with (FieldRoundsController).
 */
import {apiActionImproved, apiRetrieveImproved} from '../api';
import {formatsFor} from '../constants';

export type FieldScoring = 'stroke' | 'stableford' | 'pinfall';
export type FieldEntryStatus = 'finished' | 'WD' | 'DQ' | 'DNF';

export interface FieldEntry {
    uid: string;
    display_name: string;
    gross: number | null;
    handicap: number | null;
    net: number | null;
    points: number | null;
    detail: {holes?: (number | null)[]; games?: (number | null)[]} | null;
    /** A total typed in where there is no hole-by-hole or per-game detail. */
    total: number | null;
    /** Holes or games entered so far. */
    thru: number | null;
    status: FieldEntryStatus;
    position: number | null;
    tied: boolean;
    countback_rank: number | null;
    team_uid?: string | null;
}

export interface FieldRound {
    uid: string;
    version: number;
    name: string;
    round_number: number;
    scoring: FieldScoring;
    holes_or_games: number;
    lower_wins: boolean;
    handicap_enabled: boolean;
    division_key: string | null;
    status: 'draft' | 'in_progress' | 'completed';
    played_at: string | null;
    course: {pars: number[]; stroke_indexes: number[]} | null;
    entries: FieldEntry[];
}

export interface FieldTableRow {
    key: string;
    name: string;
    position: number | null;
    tied: boolean;
    total: number | null;
    rounds: Record<string, number | string | null>;
    status?: string | null;
    played?: number;
    all_points?: number;
}

export interface FieldTable {
    division_key: string | null;
    rounds: string[];
    rows: FieldTableRow[];
    lower_wins?: boolean;
    best_n?: number | null;
}

export interface FieldEventPayload {
    tournament: {uid: string; title: string; sport_type: string | null; format: string | null; divisions: {key: string | null; name: string}[]};
    scorings: FieldScoring[];
    rounds: FieldRound[];
    leaderboard: FieldTable[];
    series: FieldTable[];
    series_settings: {points: number[]; best_n: number | null};
}

/** A golf or ten-pin event with the flag on: its sport's formats offer the leaderboard. */
export function isFieldSport(sport: string | null | undefined): boolean {
    return !!formatsFor(sport)?.has('leaderboard');
}

/** A season series ranks the order of merit first; a leaderboard event the rounds added up. */
export const isSeriesFormat = (format: string | null | undefined): boolean => format === 'Season Series';

export const fieldRoundsApi = {
    list: (tournamentUid: string): Promise<FieldEventPayload> => apiRetrieveImproved('field-rounds' as any, {tournament_uid: tournamentUid}),
    publicLeaderboard: (tournamentUid: string): Promise<FieldEventPayload> =>
        apiRetrieveImproved(`public-field-leaderboard?tournament_uid=${encodeURIComponent(tournamentUid)}` as any, {}, {method: 'GET', body: undefined}),
    create: (tournamentUid: string, values: Record<string, unknown> = {}): Promise<FieldRound> =>
        apiActionImproved('field-rounds-create' as any, {tournament_uid: tournamentUid, values}),
    update: (round: FieldRound, values: Record<string, unknown>): Promise<FieldRound> =>
        apiActionImproved('field-rounds-update' as any, {uid: round.uid, values: {version: round.version + 1, ...values}}),
    remove: (round: FieldRound): Promise<unknown> => apiActionImproved('field-rounds-delete' as any, {uid: round.uid}),
    saveEntries: (round: FieldRound, entries: Record<string, unknown>[]): Promise<FieldRound> =>
        apiActionImproved('field-rounds-entries' as any, {uid: round.uid, entries}),
    countback: (round: FieldRound, order: string[], note: string): Promise<FieldRound> =>
        apiActionImproved('field-rounds-countback' as any, {uid: round.uid, order, note}),
    sync: (round: FieldRound): Promise<FieldRound & {added: number}> => apiActionImproved('field-rounds-sync' as any, {uid: round.uid}),
    updateSeries: (tournamentUid: string, points: number[], bestN: number | null, version: number) =>
        apiActionImproved('field-series-update' as any, {tournament_uid: tournamentUid, points, best_n: bestN, version}),
};

/** The number a card is ranked on, as the board shows it: net or gross strokes, points, or pins. */
export function shownScore(round: FieldRound, e: FieldEntry): number | null {
    if (round.scoring === 'stableford') return e.points;
    return round.handicap_enabled && e.net !== null ? e.net : e.gross;
}

/** "T3" for a shared position, "3" otherwise; nothing for WD, DQ, DNF or no score yet. */
export function positionLabel(position: number | null, tied: boolean): string {
    return position === null ? '' : `${tied ? 'T' : ''}${position}`;
}

/** Gross stroke play to par, when the round knows its pars: "-2", "E", "+5" over the holes played. */
export function toPar(round: FieldRound, e: FieldEntry): string | null {
    const pars = round.course?.pars;
    const holes = e.detail?.holes;
    if (round.scoring !== 'stroke' || !pars || !holes) return null;
    let diff = 0;
    let played = 0;
    holes.forEach((s, i) => {
        if (s !== null && s !== undefined) {
            diff += s - (pars[i] ?? 0);
            played++;
        }
    });
    if (!played) return null;
    return diff === 0 ? 'E' : diff > 0 ? `+${diff}` : String(diff);
}

export const scoringLabel = (s: FieldScoring): string => (s === 'stroke' ? 'Stroke play' : s === 'stableford' ? 'Stableford' : 'Pinfall');
